import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Icon,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, useMessage } from "../../sdk/hooks";
import { WorkspaceToolbar } from "../../components/common";
import { formatCurrency } from "../../sdk/utils/numbers";
import {
    PriceChangeScheduleStatus,
    PriceChangeSchedulePlatform,
    PriceChangeScheduleHistoryAction,
    priceChangeScheduleStatusNames,
    priceChangeSchedulePlatformNames,
    priceChangeScheduleHistoryActionNames,
} from "../../utils/enums";
import {
    getSchedule,
    updateSchedule,
    cancelSchedule,
    IPriceChangeSchedule,
} from "../../sdk/utils/api/price-change-schedule";

const Root = styled("main")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    maxWidth: 800,
    [theme.breakpoints.down("md")]: {
        maxWidth: "unset",
    },
}));

const Section = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2),
    },
}));

const SectionTitle = styled(Typography)({
    fontWeight: 600,
    marginBottom: 12,
});

const statusColorMap: Record<PriceChangeScheduleStatus, "warning" | "success" | "default" | "error"> = {
    [PriceChangeScheduleStatus.SCHEDULED]: "warning",
    [PriceChangeScheduleStatus.ACTIVE]: "success",
    [PriceChangeScheduleStatus.COMPLETED]: "default",
    [PriceChangeScheduleStatus.CANCELLED]: "error",
};

const historyActionColorMap: Record<PriceChangeScheduleHistoryAction, "default" | "success" | "warning" | "error" | "info" | "primary"> = {
    [PriceChangeScheduleHistoryAction.CREATED]: "info",
    [PriceChangeScheduleHistoryAction.ACTIVATED]: "success",
    [PriceChangeScheduleHistoryAction.DEACTIVATED]: "warning",
    [PriceChangeScheduleHistoryAction.COMPLETED]: "default",
    [PriceChangeScheduleHistoryAction.CANCELLED]: "error",
    [PriceChangeScheduleHistoryAction.UPDATED]: "primary",
    [PriceChangeScheduleHistoryAction.SYNC_SUCCESS]: "success",
    [PriceChangeScheduleHistoryAction.SYNC_FAILED]: "error",
};

const platformMap: Record<number, string> = {
    0: "Website",
    1: "Amazon",
};

const formatPaisa = (paisa: number) => formatCurrency(paisa / 100);

const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const toInputDatetime = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
};

export const PriceChangeScheduleDetailScreen: FunctionComponent = (): ReactElement => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = useAuth();
    const { showError, showSuccess } = useMessage();

    const [schedule, setSchedule] = useState<(IPriceChangeSchedule & { history?: any[] }) | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const [sellingPrice, setSellingPrice] = useState("");
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [notes, setNotes] = useState("");
    const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState("");

    const isEditable = schedule?.status === PriceChangeScheduleStatus.SCHEDULED;

    useEffect(() => {
        const fetch = async () => {
            if (!token || !id) return;
            try {
                const data = await getSchedule(token, id);
                setSchedule(data);
                setSellingPrice(String(data.sellingPrice / 100));
                setStartsAt(toInputDatetime(data.startsAt));
                setEndsAt(toInputDatetime(data.endsAt));
                setNotes(data.notes || "");
                const meta = typeof data.meta === "string" ? JSON.parse(data.meta) : data.meta;
                setNotifyEmails(meta?.notifyEmails || []);
            } catch (err: any) {
                showError("Failed to load schedule");
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [token, id]);

    const handleCancel = async () => {
        if (!token || !id) return;
        setCancelling(true);
        try {
            await cancelSchedule(token, id);
            showSuccess("Schedule cancelled");
            navigate("/price-schedules");
        } catch (err: any) {
            showError(err.response?.data?.message || err.message);
        } finally {
            setCancelling(false);
        }
    };

    const handleAddEmail = () => {
        const email = emailInput.trim();
        if (!email) return;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showError("Invalid email address");
            return;
        }
        if (notifyEmails.includes(email)) {
            showError("Email already added");
            return;
        }
        setNotifyEmails([...notifyEmails, email]);
        setEmailInput("");
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        setNotifyEmails(notifyEmails.filter((e) => e !== emailToRemove));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!schedule) {
        return (
            <Box p={3}>
                <Typography>Schedule not found</Typography>
            </Box>
        );
    }

    const statusLabel = priceChangeScheduleStatusNames[schedule.status] || "Unknown";
    const statusColor = statusColorMap[schedule.status as PriceChangeScheduleStatus] || "default";

    const handleSaveAll = async () => {
        if (!token || !id) return;
        setSaving(true);
        try {
            const updatePayload: any = { meta: { notifyEmails } };
            if (isEditable) {
                updatePayload.sellingPrice = Math.round(Number(sellingPrice) * 100);
                updatePayload.startsAt = new Date(startsAt).toISOString();
                updatePayload.endsAt = new Date(endsAt).toISOString();
                updatePayload.notes = notes;
            }
            await updateSchedule(token, id, updatePayload);
            showSuccess("Schedule saved");
        } catch (err: any) {
            showError(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <WorkspaceToolbar
                title="Price Schedule"
                enableSearch={false}
                breadcrumbs={[
                    { title: "Price Schedules", link: "/price-schedules" },
                    { title: schedule.id.slice(0, 8) + "...", link: `/price-schedules/${id}` },
                ]}
                actions={
                    <>
                        {(schedule.status === PriceChangeScheduleStatus.SCHEDULED || schedule.status === PriceChangeScheduleStatus.ACTIVE) && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleCancel}
                                disabled={cancelling}
                            >
                                {cancelling ? "Cancelling..." : "Cancel"}
                            </Button>
                        )}
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            size="small"
                            endIcon={<Icon>check_circle</Icon>}
                            onClick={handleSaveAll}
                            loading={saving}
                        >
                            Save
                        </LoadingButton>
                    </>
                }
            />
        <Root sx={{ padding: { xs: 1, sm: 2, md: 3 }, gap: { xs: 1, sm: 2 } }}>
            <Box display="flex" alignItems="center" gap={1}>
                <Chip label={statusLabel} color={statusColor} size="small" />
            </Box>

            <Section variant="outlined">
                <SectionTitle>Schedule Details</SectionTitle>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <TextField
                            label="Selling Price (₹)"
                            type="number"
                            size="small"
                            value={sellingPrice}
                            onChange={(e) => setSellingPrice(e.target.value)}
                            disabled={!isEditable}
                            fullWidth
                        />
                        <TextField
                            label="Original Price"
                            size="small"
                            value={formatPaisa(schedule.baseSellingPrice)}
                            disabled
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <TextField
                            label="Starts At"
                            type="datetime-local"
                            size="small"
                            value={startsAt}
                            onChange={(e) => setStartsAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={!isEditable}
                            fullWidth
                        />
                        <TextField
                            label="Ends At"
                            type="datetime-local"
                            size="small"
                            value={endsAt}
                            onChange={(e) => setEndsAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={!isEditable}
                            fullWidth
                        />
                    </Box>
                    <TextField
                        label="Notes"
                        size="small"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={!isEditable}
                        fullWidth
                    />
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: "center" }}>
                            Platforms:
                        </Typography>
                        {schedule.platforms?.map((p) => (
                            <Chip
                                key={p.id}
                                label={priceChangeSchedulePlatformNames[p.platform] || p.platform}
                                size="small"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                </Box>
            </Section>

            <Section variant="outlined">
                <SectionTitle>Notification Emails</SectionTitle>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    These emails will be notified when this schedule is activated or reverted.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mb: 2 }}>
                    <TextField
                        label="Email address"
                        size="small"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddEmail())}
                        fullWidth
                    />
                    <Button
                        variant="outlined"
                        onClick={handleAddEmail}
                        sx={{ minWidth: { xs: "100%", sm: "auto" } }}
                    >
                        Add
                    </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {notifyEmails.map((email) => (
                        <Chip key={email} label={email} onDelete={() => handleRemoveEmail(email)} />
                    ))}
                    {notifyEmails.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No notification emails configured for this schedule
                        </Typography>
                    )}
                </Box>
            </Section>

            {schedule.history && schedule.history.length > 0 && (
                <Section variant="outlined">
                    <SectionTitle>History</SectionTitle>
                    {schedule.history.map((entry: any, i: number) => (
                        <Box key={entry.id || i}>
                            <Box display="flex" alignItems="center" gap={1} py={1} flexWrap="wrap">
                                <Typography variant="body2">
                                    {formatDateTime(entry.createdAt)}
                                </Typography>
                                <Chip
                                    label={priceChangeScheduleHistoryActionNames[entry.status] || `Unknown (${entry.status})`}
                                    color={historyActionColorMap[entry.status as PriceChangeScheduleHistoryAction] || "default"}
                                    size="small"
                                />
                                {entry.oldSellingPrice != null && (
                                    <Typography variant="caption" color="text.secondary">
                                        {formatPaisa(entry.oldSellingPrice)} → {formatPaisa(entry.newSellingPrice)}
                                    </Typography>
                                )}
                            </Box>
                            {i < schedule.history!.length - 1 && <Divider />}
                        </Box>
                    ))}
                </Section>
            )}
        </Root>
        </>
    );
};
